/**
 * GOOGLE MAPS API OPTIMIZATION STRATEGIES
 *
 * Reduces API calls and improves performance
 */
/**
 * Check if Google Maps is already loaded globally
 * Prevents multiple script loads across page navigations
 */
export const isGoogleMapsLoaded = (): boolean => {
  return !!(
    typeof window !== "undefined" &&
    window.google &&
    window.google.maps
  );
};

/**
 * Get Google Maps load count from session
 * Helps track how many times API was called in current session
 */
export const getMapLoadCount = (): number => {
  if (typeof sessionStorage === "undefined") return 0;
  const count = sessionStorage.getItem("googleMapsLoadCount");
  return count ? parseInt(count, 10) : 0;
};

/**
 * Increment map load counter
 */
export const incrementMapLoadCount = (): void => {
  if (typeof sessionStorage === "undefined") return;
  const current = getMapLoadCount();
  sessionStorage.setItem("googleMapsLoadCount", String(current + 1));
};

/**
 * Preload Google Maps script on app initialization
 * Call this in App.tsx to warm up the cache before user navigates to map
 */
export const preloadGoogleMaps = (): void => {
  if (isGoogleMapsLoaded()) {
    return;
  }

  // Create link preload for DNS and script
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = "https://maps.googleapis.com";
  document.head.appendChild(link);

  // Preconnect to Google Maps static resources
  const linkStatic = document.createElement("link");
  linkStatic.rel = "preconnect";
  linkStatic.href = "https://maps.gstatic.com";
  document.head.appendChild(linkStatic);
};

/**
 * Get optimization recommendations based on usage
 */
export const getOptimizationTips = (): string[] => {
  const tips: string[] = [];
  const loadCount = getMapLoadCount();

  if (loadCount > 5) {
    tips.push("Consider implementing lazy loading for the map component");
  }

  if (loadCount > 10) {
    tips.push("High reload count detected - check for unnecessary re-renders");
  }

  return tips;
};

/**
 * Monitor Google Maps API usage
 */
export const logGoogleMapsUsage = (): void => {
  if (typeof window === "undefined") return;

  const loadCount = getMapLoadCount();
  const isLoaded = isGoogleMapsLoaded();

  console.log("📊 [MAPS USAGE] Session Statistics:");
  console.log(`  - API loaded: ${isLoaded ? "Yes" : "No"}`);
  console.log(`  - Load count: ${loadCount}`);
  console.log(`  - Estimated cost: $${((loadCount * 7) / 1000).toFixed(4)}`);

  const tips = getOptimizationTips();
  if (tips.length > 0) {
    console.log("💡 [MAPS] Optimization tips:");
    tips.forEach((tip) => console.log(`  - ${tip}`));
  }
};
